import {
  addDays,
  dayInZone,
  daysBetween,
  daysInMonth,
  formatCalendarDay,
  parseCalendarDay,
  wallClockTime,
  weekday,
  type CalendarDay,
} from './wall-clock';

/**
 * Repeating tasks: "every Monday check the open PRs", "every month update dependencies".
 * A task with a Repeat comes back to the top of the Board's first Column at TASK_REPEAT_HOUR on
 * each day the Repeat lands on, in the project's timezone (the hour a snooze ends). The project's hub
 * (TaskHubDO) does it, the same way it ends snoozes.
 */

export const TASK_REPEAT_HOUR = 8;

export type TaskRepeatUnit = 'day' | 'week' | 'month';

export interface TaskRepeat {
  /** Every 1 day, every 2 weeks, every 3 months… */
  every: number;
  unit: TaskRepeatUnit;
  /** `week`: the days it lands on, 0 = Sunday … 6 = Saturday; at least one. */
  weekdays?: number[];
  /** `month`: the day of the month, 1–31 (a short month uses its last day), or -1 for the last day. */
  month_day?: number;
  /** First day it may land on, "2026-10-05"; "every 2 weeks" counts from this day's week. */
  start: string;
  /**
   * Make a new task each time instead of bringing this one back. The new task takes the Repeat
   * with it, so the latest task in the series is always the one to change or stop.
   */
  new_task?: boolean;
}

/** Longest gap: every 365 days, 52 weeks or 12 months. */
export const TASK_REPEAT_MAX_EVERY: Record<TaskRepeatUnit, number> = { day: 365, week: 52, month: 12 };

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** Monday first, the way the week picker shows them. */
export const TASK_REPEAT_WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

export type TaskRepeatParse = { ok: true; repeat: TaskRepeat } | { ok: false; error: string };

/** Check a Repeat from a request, and tidy it (sorted weekdays, only the fields its unit uses). */
export function parseTaskRepeat(value: unknown): TaskRepeatParse {
  if (!value || typeof value !== 'object') return { ok: false, error: 'Repeat must be an object' };
  const raw = value as Record<string, unknown>;
  const unit = raw.unit;
  if (unit !== 'day' && unit !== 'week' && unit !== 'month') {
    return { ok: false, error: 'Repeat unit must be day, week or month' };
  }
  const every = raw.every ?? 1;
  if (typeof every !== 'number' || !Number.isInteger(every) || every < 1 || every > TASK_REPEAT_MAX_EVERY[unit]) {
    return { ok: false, error: `Repeat every 1 to ${TASK_REPEAT_MAX_EVERY[unit]} ${unit}s` };
  }
  const start = parseCalendarDay(raw.start);
  if (!start) return { ok: false, error: 'Repeat start must be a date like 2026-10-05' };

  if (raw.new_task !== undefined && typeof raw.new_task !== 'boolean') {
    return { ok: false, error: 'Repeat new_task must be true or false' };
  }
  const repeat: TaskRepeat = { every, unit, start: formatCalendarDay(start) };
  if (raw.new_task) repeat.new_task = true;
  if (unit === 'week') {
    const days = raw.weekdays;
    if (!Array.isArray(days) || !days.length || !days.every((d) => Number.isInteger(d) && d >= 0 && d <= 6)) {
      return { ok: false, error: 'Pick at least one day of the week (0 = Sunday … 6 = Saturday)' };
    }
    repeat.weekdays = [...new Set(days as number[])].sort((a, b) => a - b);
  }
  if (unit === 'month') {
    const day = raw.month_day;
    if (typeof day !== 'number' || !Number.isInteger(day) || !(day === -1 || (day >= 1 && day <= 31))) {
      return { ok: false, error: 'Repeat day of the month must be 1 to 31, or -1 for the last day' };
    }
    repeat.month_day = day;
  }
  return { ok: true, repeat };
}

/** Monday of `date`'s week. */
function mondayOf(date: CalendarDay): CalendarDay {
  return addDays(date, -((weekday(date) + 6) % 7));
}

/** Does the Repeat land on `date`? */
export function repeatLandsOn(repeat: TaskRepeat, date: CalendarDay): boolean {
  const start = parseCalendarDay(repeat.start);
  if (!start || daysBetween(start, date) < 0) return false;
  if (repeat.unit === 'day') return daysBetween(start, date) % repeat.every === 0;
  if (repeat.unit === 'week') {
    if (!repeat.weekdays?.includes(weekday(date))) return false;
    return (daysBetween(mondayOf(start), mondayOf(date)) / 7) % repeat.every === 0;
  }
  const months = (date.year - start.year) * 12 + (date.month - start.month);
  if (months % repeat.every !== 0) return false;
  const last = daysInMonth(date.year, date.month);
  const target = repeat.month_day === -1 ? last : Math.min(repeat.month_day ?? 1, last);
  return date.day === target;
}

/**
 * The next time the task comes back after `after`: TASK_REPEAT_HOUR on the next day the Repeat
 * lands on, in `timeZone`. Null only for a Repeat that never lands (it can't, once parsed).
 */
export function nextTaskRepeatAt(repeat: TaskRepeat, after: Date, timeZone: string): Date | null {
  const today = dayInZone(after, timeZone);
  const start = parseCalendarDay(repeat.start);
  if (!start) return null;
  // From today, or the start day if it's later. The longest gap is 12 months, or a year of days,
  // so a little over two years from there always finds one.
  const from = daysBetween(today, start) > 0 ? start : today;
  for (let i = 0; i <= 800; i++) {
    const day = addDays(from, i);
    if (!repeatLandsOn(repeat, day)) continue;
    const at = wallClockTime(day, TASK_REPEAT_HOUR, timeZone);
    if (at.getTime() > after.getTime()) return at;
  }
  return null;
}

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`;
}

function listDays(days: number[]): string {
  const ordered = TASK_REPEAT_WEEK_ORDER.filter((d) => days.includes(d));
  if (ordered.length === 1) return WEEKDAY_NAMES[ordered[0]];
  return ordered.map((d) => WEEKDAY_SHORT[d]).join(', ');
}

/** "Every Monday", "Every weekday", "Every 2 weeks on Mon, Thu", "Every month on the last day". */
export function describeTaskRepeat(repeat: TaskRepeat): string {
  const n = repeat.every;
  if (repeat.unit === 'day') return n === 1 ? 'Every day' : `Every ${n} days`;
  if (repeat.unit === 'week') {
    const days = repeat.weekdays ?? [];
    const weekdays = days.length === 5 && [1, 2, 3, 4, 5].every((d) => days.includes(d));
    if (n === 1 && weekdays) return 'Every weekday';
    if (n === 1 && days.length === 7) return 'Every day';
    if (n === 1) return `Every ${listDays(days)}`;
    return `Every ${n} weeks on ${listDays(days)}`;
  }
  const day = repeat.month_day === -1 ? 'the last day' : `the ${ordinal(repeat.month_day ?? 1)}`;
  return n === 1 ? `Every month on ${day}` : `Every ${n} months on ${day}`;
}
